import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { catchError, concat, debounceTime, distinctUntilChanged, filter, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmaFirmUserDto, LastAndFirstNamePipe, OrDashPipe, OrganizationDto, TooltipComponent, TruncatePipe } from 'lib';
import { NgSelectModule } from '@ng-select/ng-select';
import { EmaFirmUserSearchService } from '../../services/ema-firm-user-search.service';


@Component({
    selector: 'mmoga-ema-account-search',
    standalone: true,
    imports: [
        AsyncPipe,
        NgSelectModule,
        CommonModule,
        FormsModule,
        LastAndFirstNamePipe,
        TruncatePipe,
        OrDashPipe,
        TooltipComponent
    ],
    templateUrl: './ema-account-search.component.html',
    styleUrl: './ema-account-search.component.scss'
})
export class EmaAccountSearchComponent implements OnChanges {
    private emaFirmUserSearchService = inject(EmaFirmUserSearchService);

    currentSearchTerm = '';
    minSearchTermLength = 3;
    emaAccountsLoading = false;
    morePages = false;
    noRecordsFound = false;
    emaAccountsSearchInput$ = new Subject<string>;
    emaAccounts$: Observable<EmaFirmUserDto[]> = new Observable<EmaFirmUserDto[]>();

    @Input() readOnly = false;
    @Input() alreadySelectedEmaAccountIds: number[] = [];
    @Output() newEmaAccountSelectedEvent = new EventEmitter<EmaFirmUserDto>();
    selectedEmaAccount = 'Any';
    @Input() reset = false;
    @Input() selectedOrganization!: OrganizationDto | null;


    loadEmaAccounts(): void {
        if (this.selectedOrganization?.pod && this.selectedOrganization?.internalId) {
            this.emaAccounts$ = concat(
                this.emaAccountsSearchInput$.pipe(
                    filter(currentSearchTerm => this.checkSearchTermLength(currentSearchTerm)),
                    startWith(''),
                    distinctUntilChanged(),
                    debounceTime(800),
                    tap(() => this.emaAccountsLoading = true),
                    switchMap(searchTerm => {
                        const termToSearch = searchTerm ?? '';
                        return this.emaFirmUserSearchService.search(
                            termToSearch || '',
                            this.selectedOrganization?.pod,
                            this.selectedOrganization?.internalId,
                            this.alreadySelectedEmaAccountIds).pipe(
                            map(emaAccountResult => {
                                this.morePages = emaAccountResult.totalElements > emaAccountResult.size;
                                this.noRecordsFound = emaAccountResult.totalElements === 0;
                                return emaAccountResult.content;
                            }),
                            catchError(() => of([])),
                            tap(() => this.emaAccountsLoading = false)
                        );
                    })
                )
            );
        }
    }

    onEmaAccountChange(event: EmaFirmUserDto): void {
        if (event) {
            this.newEmaAccountSelectedEvent.emit(event);
            const lastAndFirstNamePipe = new LastAndFirstNamePipe();
            this.selectedEmaAccount = lastAndFirstNamePipe.transform(event) ?? '';
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSearchTermChange(event: { term: string; items: any[] }): void {
        if (!event.term) {
            this.resetCurrentSearch();
            return;
        }
        if (this.currentSearchTerm !== event.term) {
            this.currentSearchTerm = event.term;
        }
    }

    resetCurrentSearch(): void {
        this.loadEmaAccounts();
        this.morePages = false;
        this.noRecordsFound = false;
        this.selectedEmaAccount = 'Any';
        this.newEmaAccountSelectedEvent.emit(undefined);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes[ 'selectedOrganization' ]) {
            this.resetCurrentSearch();
        }
    }

    onDropdownOpen() {
        this.emaAccountsSearchInput$.next('');
        this.loadEmaAccounts();
        this.selectedEmaAccount = '';
    }

    onDropdownClose() {
        if (this.selectedEmaAccount === '') {
            this.selectedEmaAccount = 'Any';
            this.newEmaAccountSelectedEvent.emit(undefined);
        }
    }

    private checkSearchTermLength(searchTerm: string): boolean {
        return searchTerm !== null && (searchTerm.length === 0 || searchTerm.length >= this.minSearchTermLength);
    }
}
